'use client';

import { useState } from 'react';
import { Bell, Check, Loader2 } from 'lucide-react';
import { toast } from '@/lib/toast';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.autofrance.se/api';

interface Props {
  productId: number;
  productName: string;
}

/**
 * Email signup so the customer is notified when an out-of-stock product
 * returns. Captures otherwise-lost demand without committing to backorder.
 */
export function StockNotifyForm({ productId, productName }: Props) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error('Ange en giltig e-postadress');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/track/stock-notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: productId, email: value }),
      });
      if (!res.ok) throw new Error('failed');
      setSubmitted(true);
    } catch {
      toast.error('Något gick fel — försök igen om en stund');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-xl border-2 border-green-300 bg-green-50 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="shrink-0 h-10 w-10 rounded-lg bg-green-600 flex items-center justify-center">
            <Check className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-semibold text-green-900">Vi mejlar dig direkt när produkten är tillbaka i lager.</p>
            <p className="text-xs text-green-800 mt-0.5">
              Du behöver inte göra något mer — håll utkik i inkorgen.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 sm:p-5">
      <div className="flex items-start gap-3 mb-3">
        <div className="shrink-0 h-10 w-10 rounded-lg bg-amber-200 flex items-center justify-center">
          <Bell className="h-5 w-5 text-amber-800" />
        </div>
        <div>
          <p className="font-semibold text-amber-900 leading-tight">
            Få mejl när {productName.slice(0, 40)}{productName.length > 40 ? '…' : ''} är tillbaka i lager
          </p>
          <p className="text-xs text-amber-800 mt-0.5">
            Vi skickar bara ett enda mejl och bara om delen tar in. Ingen prenumeration.
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="namn@exempel.se"
          className="flex-1 h-11 px-3 rounded-lg border border-amber-300 bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none text-base"
        />
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 px-5 h-11 rounded-lg bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-semibold text-sm"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
          Meddela mig
        </button>
      </form>
    </div>
  );
}
